"use client";

import { useEffect, useState } from "react";

const BANNER_HEIGHT_PX = 32;

/**
 * OfflineIndicator
 *
 * A thin top banner that tracks the browser's online/offline state.
 *
 *   Offline  → amber banner: "You're offline — viewing saved content..."
 *   Restored → green banner for 3 seconds: "Back online — syncing changes..."
 *   Online   → renders nothing
 *
 * Publishes its height as `--offline-banner-height` on <html> so the rest of
 * the app (body padding, fixed headers, sidebar) can shift down instead of
 * being overlapped.
 */
export default function OfflineIndicator() {
  const [isOnline, setIsOnline] = useState(true);
  const [showRestored, setShowRestored] = useState(false);

  useEffect(() => {
    // Sync with actual network state on mount (SSR defaults to true)
    setIsOnline(navigator.onLine);

    // Held in the effect scope so the cleanup can always cancel it,
    // even if the component unmounts mid-countdown or the user goes
    // offline again before the 3-second banner disappears.
    let restoredTimer: ReturnType<typeof setTimeout> | null = null;

    const handleOnline = () => {
      setIsOnline(true);
      setShowRestored(true);
      // Clear any previous timer before starting a new one
      if (restoredTimer !== null) clearTimeout(restoredTimer);
      restoredTimer = setTimeout(() => setShowRestored(false), 3000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowRestored(false);
      // Cancel the "back online" countdown if we drop offline again
      if (restoredTimer !== null) {
        clearTimeout(restoredTimer);
        restoredTimer = null;
      }
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      // Cancel any in-flight timer on unmount
      if (restoredTimer !== null) clearTimeout(restoredTimer);
    };
  }, []);

  const bannerVisible = !isOnline || showRestored;

  // Drives body padding + header/sidebar offsets via CSS var so the app shifts
  // down rather than being covered by the fixed banner. The published height
  // includes `env(safe-area-inset-top)` so that on notched devices (where the
  // banner itself pads around the notch via baseStyle.paddingTop below) the
  // chrome underneath also clears the notch region — otherwise headers fixed
  // at `top: var(--offline-banner-height)` would slide under the banner's
  // safe-area padding zone.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(
      "--offline-banner-height",
      bannerVisible ? `calc(${BANNER_HEIGHT_PX}px + env(safe-area-inset-top, 0px))` : "0px",
    );
    return () => {
      root.style.setProperty("--offline-banner-height", "0px");
    };
  }, [bannerVisible]);

  // Online and no recent restoration — render nothing
  if (isOnline && !showRestored) return null;

  const baseStyle = {
    position: "fixed" as const,
    top: 0,
    left: 0,
    right: 0,
    zIndex: 9999,
    // `minHeight` instead of fixed `height` lets the banner grow when
    // `paddingTop: env(safe-area-inset-top)` adds the notch inset on iOS.
    // Without this, padding would eat into the 32px content area on notched
    // devices and the message text could clip behind the notch.
    minHeight: `${BANNER_HEIGHT_PX}px`,
    display: "flex" as const,
    alignItems: "center" as const,
    justifyContent: "center" as const,
    textAlign: "center" as const,
    padding: "0 16px",
    paddingTop: "env(safe-area-inset-top, 0px)",
    fontSize: "13px",
    fontWeight: 500,
    lineHeight: "1.4",
    boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
  };

  if (!isOnline) {
    return (
      <div
        role="status"
        aria-live="polite"
        style={{ ...baseStyle, backgroundColor: "#f59e0b", color: "#1c1917" }}
      >
        You&apos;re offline — viewing saved content. Changes will sync when reconnected.
      </div>
    );
  }

  // showRestored === true
  return (
    <div
      role="status"
      aria-live="polite"
      style={{ ...baseStyle, backgroundColor: "#22c55e", color: "#052e16" }}
    >
      Back online — syncing changes...
    </div>
  );
}
